import { BoxContainer, BoxContainerProps, CircleIcon, FluidCanvas, MinimapCropMinimap, MinimapCropPan, MinimapCropProvider, MinimapCropViewport, PrimaryButton, ReactIcons, useMinimapCropContext, VideoControlsOverlay } from "@kalyzee/kalyzee-ui";

import workBoxStyles from "./WorkBox.module.css";
import styles from "./PlayerWorkBox.module.css";
import React from "react";
import clsx from "clsx";
import { Touchable, UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import { LayoutType } from "../../interfaces/ui";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useSettings } from "../../hooks/settings";
import { UserCardAvatarArea } from "../common/UserCardAvatarArea";
import { InteractionIsolate } from "../utils/InteractionIsolate";

/**
 * Round action button — used both in the topbar (always visible) and as a
 * centered overlay on hover. Same shape, different render slot.
 */
export interface PlayerActionItem {
  icon: React.ReactNode;
  onClick: () => void;
  label?: string;
  /** Optional key to stabilize React reconciliation (defaults to index). */
  key?: string;
}

/** @deprecated alias kept for compat — prefer {@link PlayerActionItem}. */
export type PlayerTopbarAction = PlayerActionItem;

/**
 * PTZ camera controls — directional pad + zoom buttons rendered as a full
 * overlay over the player. Each callback receives `true` on press-in and
 * `false` on press-out so the consumer can implement hold-to-move.
 */
export interface PlayerWorkBoxControlsOverlay {
  type: "controls";
  onRight?: (on: boolean) => void;
  onLeft?: (on: boolean) => void;
  onUp?: (on: boolean) => void;
  onDown?: (on: boolean) => void;
  onZoomIn?: (on: boolean) => void;
  onZoomOut?: (on: boolean) => void;
}

/**
 * Click-anywhere selection overlay — full-area dark overlay with a centered
 * directional icon. Use this when you want the entire player area to act as
 * a focus target, instead of (or in addition to) a small action button.
 */
export interface PlayerWorkBoxSelectionOverlay {
  type: "selection";
  onSelect: () => void;
  /** Drives the directional icon (down/up/left/right). */
  layout: LayoutType;
}

export type PlayerWorkBoxOverlayContent =
  | React.ReactNode
  | PlayerWorkBoxControlsOverlay
  | PlayerWorkBoxSelectionOverlay;

/**
 * Interactive minimap/crop config. When `enabled`, the player can be shown
 * cropped to a navigable viewport with an overlay minimap, toggled via an
 * overlay button — PlayerWorkBox owns the whole feature.
 */
export interface PlayerWorkBoxMinimap {
  enabled?: boolean;
  /** Content aspect ratio (width / height) for the crop. */
  ratio?: number;
  /**
   * Alternative minimap content (defaults to the player itself). Needed when
   * the player cannot be mounted twice — e.g. the VOD <video> elements are
   * ref-controlled by MultipleVideoPlayer, so only the viewport may render them.
   */
  minimapContent?: React.ReactNode;
  /** Controlled crop pan: a value enables crop mode (and is the scroll position);
   *  `undefined` means crop mode is off (whole frame shown). */
  pan?: MinimapCropPan;
  /** Update the pan, or pass `undefined` to disable crop mode. */
  onPanChange?: (pan: MinimapCropPan | undefined) => void;
  /**
   * Where the toggle + minimap stack anchors. `top` inverts the stack (minimap
   * above, exit toggle below) for hosts whose bottom edge is busy (e.g. the
   * VOD timeline bar). Defaults to `bottom`.
   */
  position?: "bottom" | "top";
}

/**
 * Neutral explanation of what a tile is showing — "vidéo désactivée", "source retirée",
 * "piste vidéo absente". Deliberately NOT a failure: `broken` still wins, so a real
 * playback failure keeps reading as one.
 */
export interface PlayerWorkBoxEventLogEntry {
  time: string;
  label: string;
  /** Already happened at the current playhead; future entries are dimmed. */
  past: boolean;
}

export interface PlayerWorkBoxNotice {
  /** The caller's vocabulary. Carried for the caller's own bookkeeping, not read here. */
  kind: string;
  label: string;
  /** Second line — e.g. the wall-clock instant the state was established. */
  detail?: string;
}

const isTypedOverlay = (value: unknown): value is PlayerWorkBoxControlsOverlay | PlayerWorkBoxSelectionOverlay =>
  !!value && typeof value === "object" && "type" in (value as object);

const focusIconForLayout = (layout: LayoutType) => {
  switch (layout) {
    case LayoutType.Top: return ReactIcons.SelectMainDown;
    case LayoutType.Bottom: return ReactIcons.SelectMainVertical;
    case LayoutType.Left: return ReactIcons.SelectMainRight;
    case LayoutType.Right: return ReactIcons.SelectMainHorizontal;
    default: return ReactIcons.Expand;
  }
};

export interface PlayerWorkBoxProps {
  children: React.ReactNode;
  label: string;
  labelInfo?: string;
  userDisplayName?: string;
  avatar?: string;
  /** Owner of the stream, so the disabled-camera avatar can open their card. */
  userId?: string;
  overlayMode?: BoxContainerProps["mode"];
  /**
   * Free-form overlay content (e.g. PTZ controls). Renders inside the
   * topbar's overlay layer below the topbar itself.
   */
  overlayContent?: PlayerWorkBoxOverlayContent;
  /**
   * Round action buttons appended to the topbar's right slot, after the
   * existing camera/mic media buttons.
   */
  topbarActions?: PlayerActionItem[];
  extraHeaderContent?: React.ReactNode;
  isScreenSource?: boolean;
  /** Non-selected secondary (strip) player: hides the source info indicators. */
  isSidePlayer?: boolean;
  /** Interactive minimap/crop feature (owned entirely by PlayerWorkBox). */
  minimap?: PlayerWorkBoxMinimap;
  className?: string;
  topBarClassName?: string;
  audio?: boolean;
  video?: boolean;
  isMe?: boolean;
  isHighlighted?: boolean;
  isPlayingSound?: boolean;
  isSmall?: boolean;
  /** Shown alone, edge to edge (browser fullscreen): no frame, no rounded corners. */
  isFullscreen?: boolean;
  /**
   * Nothing laid over the player: no topbar, no overlay content, no minimap controls. For hosts
   * that show and hide every overlay together (mobile: with the control bar).
   */
  overlaysHidden?: boolean;
  boxProps?: React.HTMLAttributes<HTMLDivElement>;
  containerRef?: React.RefObject<HTMLDivElement>;
  idleAnimation?: boolean;
  loading?: boolean;
  broken?: boolean;
  /**
   * Why this tile is showing nothing, when the reason is known and is not a failure.
   * Full-cover overlay, suppressed by `broken`; also suppresses the loading spinner
   * (a tile whose source was cut has nothing to buffer). Drawn like the disabled-camera
   * cover — the person's avatar when the tile knows them, an icon otherwise — so a replay
   * reads like the live meeting did. A cut mic is not a notice: it is `audio={false}`.
   */
  notice?: PlayerWorkBoxNotice;
  /**
   * Dev-only history of this tile's own state changes, oldest first. Drawn over the
   * picture so a transition is read against the frame it applies to; entries the
   * playhead has not reached yet are dimmed. Empty or absent draws nothing.
   */
  eventLog?: PlayerWorkBoxEventLogEntry[];
  mediaButtons?: boolean;
  localStream?: boolean;
  onChangeAudio?: () => void;
  onChangeVideo?: () => void;
}

export const PlayerWorkBox = React.memo(({
  children,
  label,
  labelInfo,
  userDisplayName,
  avatar,
  userId,
  overlayMode,
  overlayContent,
  topbarActions,
  extraHeaderContent,
  isScreenSource,
  isSidePlayer,
  minimap,
  className,
  topBarClassName,
  audio,
  video,
  isMe,
  isHighlighted,
  isPlayingSound,
  isSmall,
  isFullscreen,
  overlaysHidden,
  boxProps,
  containerRef,
  idleAnimation,
  loading,
  broken,
  notice,
  eventLog,
  mediaButtons,
  localStream,
  onChangeAudio,
  onChangeVideo,
}: PlayerWorkBoxProps) => {
  const isMobile = useAppStoreSelector(state => state.isMobile);
  const [settings] = useSettings();

  // Crop ("wide") mode = the minimap feature is enabled AND a pan is set. The crop applies
  // wherever the source renders; its toggle (topbar) and the minimap only show on non-strip tiles.
  const cropMode = !!minimap?.enabled && minimap.pan !== undefined;
  const showMinimapUi = !!minimap?.enabled && !isSidePlayer && !overlaysHidden;

  const left = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (extraHeaderContent) {
      result.push(extraHeaderContent);
    }
    if (isHighlighted) {
      result.push(<ReactIcons.Hand2 className={styles.nameIcon} />);
    } else if (isMe) {
      result.push(<ReactIcons.Home className={styles.nameIcon} />);
    }
    result.push(<span key="label" className={styles.userLabel}>{label}</span>);
    // labelInfo (device name) and the screen badge are dropped on mobile —
    // the topbar gets cramped quickly next to the username and the media
    // buttons. The label alone (user name) is enough to identify the source.
    if (labelInfo && !isMobile) {
      result.push(
        <span key="youLabel" className={workBoxStyles.labelInfo}>{labelInfo}</span>
      );
    }
    if (!isSidePlayer && !isMobile && isScreenSource) {
      result.push(
        <span key="screen-badge" className={styles.screenBadge}>
          <ReactIcons.ComputerScreen />
          {'Écran'}
        </span>
      );
    }
    return result;
  }, [extraHeaderContent, isHighlighted, isMe, isScreenSource, isSidePlayer, isMobile, label, labelInfo]);

  const right = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (video !== undefined && mediaButtons) {
      result.push(
        <PrimaryButton
          key="video"
          onClick={onChangeVideo}
        >
          <CircleIcon.Variants.Camera
            className={styles.mediaBtn}
            disabled={!video}
          />
        </PrimaryButton>
      );
    }
    if (audio !== undefined && mediaButtons) {
      result.push(
        <PrimaryButton
          key="audio"
          onClick={onChangeAudio}
        >
          <CircleIcon.Variants.Mic
            className={styles.mediaBtn}
            disabled={!audio}
          />
        </PrimaryButton>
      );
    }
    // Crop ("wide") mode toggle, in pastel green: the one action that changes what is shown.
    if (showMinimapUi) {
      const label = cropMode ? "Afficher toute l'image" : "Recadrer (mode interactif)";
      result.push(
        <InteractionIsolate key="wide">
          <PrimaryButton
            onClick={() => minimap?.onPanChange?.(cropMode ? undefined : { x: 0.5, y: 0.5 })}
            nativeProps={{ "aria-label": label, title: label }}
          >
            <CircleIcon.Variants.UltraWide active={cropMode} className={clsx(styles.mediaBtn, styles.wideBtn, cropMode && styles.wideBtnActive)} />
          </PrimaryButton>
        </InteractionIsolate>
      );
    }
    // Isolated: inside a player that toggles its control bar on touch (VOD), pressing an action
    // (fullscreen...) is not a tap on the video.
    topbarActions?.forEach((action, idx) => {
      result.push(
        <InteractionIsolate key={action.key ?? `topbar-action-${idx}`}>
          <PrimaryButton
            onClick={action.onClick}
            nativeProps={action.label ? { "aria-label": action.label, title: action.label } : undefined}
          >
            <CircleIcon icon={action.icon} className={styles.mediaBtn} />
          </PrimaryButton>
        </InteractionIsolate>
      );
    });
    return result;
  }, [video, mediaButtons, audio, onChangeVideo, onChangeAudio, topbarActions, showMinimapUi, cropMode, minimap]);

  const renderDisabedCameraContent = () => {
    if (video) return null;
    return (
      <div className={styles.disabledCameraOverlay}>
        <UserCardAvatarArea user={userId ? { id: userId, username: userDisplayName, avatar } : undefined}>
          <UserAvatar
            mode="image"
            image={avatar}
            username={userDisplayName}
            className={styles.avatar}
          />
        </UserCardAvatarArea>
        <span className={styles.disabledCameraLabel}>{'La vidéo est désactivée'}</span>
      </div>
    );
  }

  /**
   * Muted mic, bottom-right of the picture. Deliberately not in the topbar: that
   * one carries the mic *button* (only when `mediaButtons`, and it fades away
   * with the overlay), while whether someone can be heard has to stay readable
   * at a glance. `undefined` means the host has nothing to report — a replay whose
   * file carries no media history — which is not the same as a muted one.
   */
  const renderMutedIndicator = () => {
    if (audio !== false) return null;
    return (
      <div className={styles.mutedIndicator} title="Micro coupé" aria-label="Micro coupé">
        <CircleIcon.Variants.Mic disabled className={styles.mutedIndicatorIcon} />
      </div>
    );
  };

  const renderIdleAnimation = () => {
    if (!idleAnimation) return null;
    return (
      <div className={styles.idleAnimationContainer}>
        <FluidCanvas speed={0.5} shapeSize="big" />
      </div>
    );
  };

  const renderLoadingOverlay = () => {
    // A tile with a notice is not waiting for bytes — it was cut or muted at the
    // source. A spinner over it would promise something is coming.
    if (broken || notice || !loading) return null;
    return (
      <div className={styles.loadingOverlay}>
        <ReactIcons.Spinner className={styles.spinner} />
      </div>
    );
  };

  const renderBrokenOverlay = () => {
    if (!broken) return null;
    return (
      <div className={styles.brokenOverlay}>
        <div className={styles.brokenMessage}>Impossible de lire le flux vidéo</div>
      </div>
    );
  };

  const renderNoticeOverlay = () => {
    // `broken` wins: a genuine playback failure must not be dressed up as a
    // deliberate cut.
    if (broken || !notice) return null;
    // The same face the live tile shows over a switched-off camera, opening the same
    // user card; a tile that knows nobody (a room camera) gets the icon instead.
    const knowsUser = !!(userId || avatar || userDisplayName);
    return (
      <div className={styles.noticeOverlay}>
        {knowsUser ? (
          <UserCardAvatarArea user={userId ? { id: userId, username: userDisplayName, avatar } : undefined}>
            <UserAvatar
              mode="image"
              image={avatar}
              username={userDisplayName}
              className={styles.avatar}
            />
          </UserCardAvatarArea>
        ) : (
          <ReactIcons.CameraOff className={styles.noticeIcon} />
        )}
        <div className={styles.noticeMessage}>{notice.label}</div>
        {notice.detail && <div className={styles.noticeDetail}>{notice.detail}</div>}
      </div>
    );
  };

  const renderEventLog = () => {
    if (!eventLog?.length) return null;
    return (
      <div className={styles.eventLog}>
        {eventLog.map((entry, i) => (
          <div
            key={`${entry.time}-${i}`}
            className={clsx(styles.eventLogRow, !entry.past && styles.eventLogRowFuture)}
          >
            <span className={styles.eventLogTime}>{entry.time}</span>
            <span className={styles.eventLogLabel}>{entry.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const resolvedOverlayContent: React.ReactNode = (() => {
    if (!overlayContent || overlaysHidden) return undefined;
    if (!isTypedOverlay(overlayContent)) return overlayContent;
    if (overlayContent.type === "controls") {
      return (
        <VideoControlsOverlay
          onUp={overlayContent.onUp}
          onDown={overlayContent.onDown}
          onLeft={overlayContent.onLeft}
          onRight={overlayContent.onRight}
          onZoomIn={overlayContent.onZoomIn}
          onZoomOut={overlayContent.onZoomOut}
        >
          <></>
        </VideoControlsOverlay>
      );
    }
    if (overlayContent.type === "selection") {
      const Icon = focusIconForLayout(overlayContent.layout);
      return (
        <Touchable onClick={overlayContent.onSelect} className={styles.selection}>
          <Icon style={{ color: "white" }} />
        </Touchable>
      );
    }
    return undefined;
  })();

  const hasPtzControls = isTypedOverlay(overlayContent) && overlayContent.type === "controls";

  /* ----------------------------- Minimap / crop ---------------------------- */
  // Let drags reach the viewport in crop mode — but not when PTZ controls or a
  // side-player selection overlay need the clicks.
  const overlayClickThrough = cropMode && !hasPtzControls && !isSidePlayer;

  const minimapOnTop = minimap?.position === "top";

  // The minimap itself, in crop mode only (its toggle is in the topbar). Rendered inside the
  // MinimapCrop provider, which crop mode wraps the box in below.
  const minimapOverlay = showMinimapUi && cropMode ? (
    <div className={styles.minimapBox}>
      <MinimapCropMinimap minimapOpacity={1} />
    </div>
  ) : null;

  // The minimap sits at the bottom (or top when `position: "top"`), lifted clear
  // of the PTZ row when controls are present, and rendered last so it stays
  // draggable on top.
  const finalOverlayContent: React.ReactNode = minimapOverlay ? (
    <div className={styles.overlayLayer}>
      {resolvedOverlayContent}
      <div
        className={clsx(
          styles.minimapSlot,
          minimapOnTop ? styles.minimapSlotTop : hasPtzControls && styles.minimapSlotWithControls,
        )}
      >
        {minimapOverlay}
      </div>
    </div>
  ) : resolvedOverlayContent;

  const box = (
    <BoxContainer
      className={clsx({
        [styles.container]: true,
        [styles.highlighted]: isHighlighted,
        [styles.playingSound]: isPlayingSound,
        [styles.small]: isSmall,
        [styles.mobile]: isMobile,
        [styles.fullscreen]: isFullscreen,
        [styles.overlaysHidden]: overlaysHidden,
      }, className)}
      topBarProps={{
        left,
        center: [],
        right: right.length ? right : undefined,
        className: clsx({
          [styles.playerTopBar]: true,
          [styles.transparent]: !video,
        }, topBarClassName),
      }}
      mode={overlayMode}
      overlayContent={finalOverlayContent}
      hideOverlayAfterMs={2000}
      overlayClickThrough={overlayClickThrough}
      nativeProps={boxProps}
      containerRef={containerRef}
    >
      {/* In crop mode the player (`children`) is the MinimapCrop content, rendered
          by the viewport (here) and the minimap; otherwise it's rendered directly. */}
      {cropMode ? <MinimapCropViewport overflowMode="x" style={{ width: "100%", height: "100%" }} /> : children}
      {renderDisabedCameraContent()}
      {renderMutedIndicator()}
      {renderIdleAnimation()}
      {renderLoadingOverlay()}
      {renderNoticeOverlay()}
      {renderBrokenOverlay()}
      {renderEventLog()}
      {localStream && settings.dev && (
        <div style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          color: "white",
          fontSize: "0.75em",
          backgroundColor: "#00000088",
          padding: "2px 5px",
          borderRadius: 5,
        }}>
          local-stream
        </div>
      )}
    </BoxContainer>
  );

  if (!cropMode) return box;

  return (
    <MinimapCropProvider
      content={children}
      minimapContent={minimap?.minimapContent}
      contentAspectRatio={minimap?.ratio ?? 16 / 9}
      pan={minimap?.pan}
      onPanChange={(p) => minimap?.onPanChange?.(p)}
      // Horizontal only: no zoom, so nothing ever overflows vertically. Free: pinch/wheel zoom
      // (up to 4x) makes both axes pannable.
      maxZoom={settings.ui?.minimapPan === "free" ? 4 : 1}
    >
      {box}
    </MinimapCropProvider>
  );
});
